import type {
  FieldMeta,
  FieldOption,
  PrintSettings,
  RecordRange,
  SortDirection,
  TableContext,
} from '../api/types';
import { DEFAULT_PRINT_SETTINGS } from '../utils/constants';

export interface SortState {
  fieldId: string | null;
  direction: SortDirection;
}

export type AppMode = 'table' | 'contract';

/** Top-level page. Drives the App router; everything stays in one panel. */
export type AppView = 'home' | 'editor' | 'templates' | 'preview' | 'store';

export interface AppState {
  mode: AppMode;
  view: AppView;
  /** When set, editor / templates pages know which template they're acting on. */
  editingTemplateId: string | null;
  context: TableContext | null;
  fieldOptions: FieldOption[];
  range: RecordRange;
  sort: SortState;
  settings: PrintSettings;
  loading: boolean;
  error: string | null;
  contractRecordId: string | null;
}

export const initialState: AppState = {
  mode: 'contract',
  view: 'home',
  editingTemplateId: null,
  context: null,
  fieldOptions: [],
  range: 'visible',
  sort: { fieldId: null, direction: 'asc' },
  settings: DEFAULT_PRINT_SETTINGS,
  loading: false,
  error: null,
  contractRecordId: null,
};

export type AppAction =
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_CONTEXT'; context: TableContext }
  | { type: 'LOAD_FIELDS'; metas: FieldMeta[] }
  | { type: 'TOGGLE_FIELD'; fieldId: string }
  | { type: 'TOGGLE_ALL'; checked: boolean }
  | { type: 'INVERT_FIELDS' }
  | { type: 'REORDER_FIELDS'; activeId: string; overId: string }
  | { type: 'FOLLOW_VIEW_ORDER'; visibleFieldIds: string[] }
  | { type: 'SET_RANGE'; range: RecordRange }
  | { type: 'SET_SORT'; sort: SortState }
  | { type: 'UPDATE_SETTINGS'; settings: Partial<PrintSettings> }
  | { type: 'APPLY_TEMPLATE'; fieldIds: string[]; settings: PrintSettings }
  | { type: 'SET_MODE'; mode: AppMode }
  | { type: 'SET_CONTRACT_RECORD_ID'; recordId: string | null }
  | { type: 'NAV'; view: AppView; editingTemplateId?: string | null };

function renumber(options: FieldOption[]): FieldOption[] {
  return options.map((opt, idx) => ({ ...opt, order: idx }));
}

export function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.loading };

    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'SET_CONTEXT':
      return { ...state, context: action.context };

    case 'LOAD_FIELDS': {
      const fieldOptions: FieldOption[] = action.metas.map((m, idx) => ({
        id: m.id,
        name: m.name,
        type: m.type,
        checked: true,
        order: idx,
      }));
      return { ...state, fieldOptions };
    }

    case 'TOGGLE_FIELD':
      return {
        ...state,
        fieldOptions: state.fieldOptions.map((f) =>
          f.id === action.fieldId ? { ...f, checked: !f.checked } : f,
        ),
      };

    case 'TOGGLE_ALL':
      return {
        ...state,
        fieldOptions: state.fieldOptions.map((f) => ({ ...f, checked: action.checked })),
      };

    case 'INVERT_FIELDS':
      return {
        ...state,
        fieldOptions: state.fieldOptions.map((f) => ({ ...f, checked: !f.checked })),
      };

    case 'REORDER_FIELDS': {
      const ordered = [...state.fieldOptions].sort((a, b) => a.order - b.order);
      const fromIdx = ordered.findIndex((f) => f.id === action.activeId);
      const toIdx = ordered.findIndex((f) => f.id === action.overId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return state;
      const [moved] = ordered.splice(fromIdx, 1);
      if (!moved) return state;
      ordered.splice(toIdx, 0, moved);
      return { ...state, fieldOptions: renumber(ordered) };
    }

    case 'FOLLOW_VIEW_ORDER': {
      const indexById = new Map<string, number>();
      action.visibleFieldIds.forEach((id, i) => indexById.set(id, i));
      const ordered = [...state.fieldOptions].sort((a, b) => {
        const ai = indexById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const bi = indexById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return a.order - b.order;
      });
      return { ...state, fieldOptions: renumber(ordered) };
    }

    case 'SET_RANGE':
      return { ...state, range: action.range };

    case 'SET_SORT':
      return { ...state, sort: action.sort };

    case 'UPDATE_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.settings } };

    case 'APPLY_TEMPLATE': {
      const idSet = new Set(action.fieldIds);
      const indexById = new Map<string, number>();
      action.fieldIds.forEach((id, i) => indexById.set(id, i));

      const inList = state.fieldOptions
        .filter((f) => idSet.has(f.id))
        .sort(
          (a, b) =>
            (indexById.get(a.id) ?? 0) - (indexById.get(b.id) ?? 0),
        )
        .map((f) => ({ ...f, checked: true }));

      const notInList = state.fieldOptions
        .filter((f) => !idSet.has(f.id))
        .sort((a, b) => a.order - b.order)
        .map((f) => ({ ...f, checked: false }));

      return {
        ...state,
        fieldOptions: renumber([...inList, ...notInList]),
        settings: { ...action.settings },
      };
    }

    case 'SET_MODE':
      return { ...state, mode: action.mode };

    case 'SET_CONTRACT_RECORD_ID':
      return { ...state, contractRecordId: action.recordId };

    case 'NAV':
      return {
        ...state,
        view: action.view,
        editingTemplateId:
          action.editingTemplateId !== undefined
            ? action.editingTemplateId
            : state.editingTemplateId,
      };

    default:
      return state;
  }
}
